<template>
  <div class="file-select-wrapper">
    <div v-if="!uploadAsFileSelect && uploadedFiles.fileNames.length !== 0" class="file-container" :id="`${id}-uploaded-container`">
      <div v-for="file in uploadedFiles.fileNames" :key="file" class="file uploaded">
        <div class="uploaded"></div>
        <div class="file-block">
          <span class="file-name">{{ file }}</span>
          <span class="upload-status">Uploaded successfully</span>
        </div>
      </div>
    </div>
    <div v-if="files.length !== 0 && uploadStatus !== LoadingStatus.ERROR">
      <div v-for="(file, index) in files" :key="`${file.name}-${index}`">
        <div class="file-container" :id="`${id}-selected-container`">
          <div
            v-if="
              v$.files.$error &&
              (!isValidFileUploadSize(file) || !isValidFileUploadType(file) || !isValidFileUploadName(file))
            "
            class="file upload-error"
          >
            <div class="file-block">
              <span class="file-name">{{ file.name }}</span>
              <span v-if="!isValidFileUploadType(file)" class="error-status">
                The file type you have selected is not accepted. We accept .jpg, .png, .gif and .pdf files. Please remove this file and upload another document.
              </span>
              <span v-else-if="!isValidFileUploadSize(file)" class="error-status">
                This document is too big to be uploaded. Individual documents cannot exceed 32MB.
              </span>
              <span v-else-if="!isValidFileUploadName(file)" class="error-status">
                Sorry, the file name contains an invalid character(s) (i.e: ’ , : or / ). Please remove the invalid character(s) and try again.
              </span>
            </div>
            <a
              v-show="uploadStatus !== LoadingStatus.PENDING"
              role="button"
              class="remove-file"
              :aria-label="`Remove file: ${file.name}`"
              @click.prevent="removeSelectedFile(index)"
              href="#"
            ></a>
          </div>
          <div v-else class="file" :class="uploadStatus === LoadingStatus.PENDING ? 'uploading' : ''">
            <div v-if="uploadStatus !== LoadingStatus.PENDING && !['image/jpeg', 'image/png'].includes(fileTypes[index])" class="default"></div>
            <img v-if="uploadStatus !== LoadingStatus.PENDING && ['image/jpeg', 'image/png'].includes(fileTypes[index])" :src="fileImgs[index]" class="preview" />

            <Spinner v-if="uploadStatus === LoadingStatus.PENDING" class="spinner" />
            <span v-if="uploadStatus === LoadingStatus.PENDING" class="uploading">Uploading</span>

            <div class="file-block">
              <span class="file-name">{{ file.name }}</span>
              <span v-if="!uploadAsFileSelect && uploadStatus !== LoadingStatus.PENDING" class="upload-status">Not yet uploaded</span>
            </div>
            <a
              v-show="uploadStatus !== LoadingStatus.PENDING"
              role="button"
              class="remove-file"
              :aria-label="`Remove file: ${file.name}`"
              @click.prevent="removeSelectedFile(index)"
              href="#"
            ></a>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="uploadStatus === LoadingStatus.ERROR" class="file-container" :id="`${id}-error-container`">
      <div class="file upload-error">
        <div class="file-block">
          <span>Oops! Something went wrong. {{ errorMessage }}</span>
        </div>
        <a role="button" aria-label="Remove file" class="remove-file" @click.prevent="updateStatus" href="#"></a>
      </div>
    </div>
    <input ref="file-uploader" aria-label="File uploader" :id="id" type="file" @change="addSelectedFile" accept="image/png, image/jpg, .gif, .pdf" multiple />
    <Alert :for="id" v-if="v$.files.$error && v$.files.isValidTotalFileUploadSize.$invalid" alertType="error" class="inline-message">
      <div>
        <p>The total of all documents cannot exceed 128MB. Please try again using the allowed file types - *.jpg, *.png, *.gif. *.pdf</p>
      </div>
    </Alert>

    <div :class="files.length === 0 ? 'button-container reduce-margin' : 'button-container'">
      <label :for="id" class="button secondary">
        <div class="icon-container">
          <svg><use xlink:href="/assets/public/images/icons.svg#upload"></use></svg>
        </div>
        Browse
      </label>
      <button v-if="files.length !== 0 && !uploadAsFileSelect" @click="uploadFiles" :disabled="v$.files.$error" class="upload-button">Upload</button>
      <p class="no-file" v-if="files.length === 0">No file selected</p>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { LoadingStatus } from '@general/js/enums';
import { isValidFileUploadSize, isValidTotalFileUploadSize, isValidFileUploadType, isValidFileUploadName } from '@general/js/validators';
import { Alert } from '@/components';
import Spinner from './Spinner.vue';

export default {
  name: 'FileSelect',

  props: {
    id: {
      type: String,
      default: () => `${crypto.randomUUID()}-select-file`,
    },

    uploadedFiles: {
      type: Object,
      required: true,
    },

    uploadStatus: {
      type: String,
      default: '',
    },

    errorMessage: {
      type: String,
      default: 'Something went wrong.',
    },

    /**
     * If this is true, is considered upload as file selected.
     * As result, `Upload` button will not display and `emit('upload')` will be triggered as file is selected.
     *
     * Use this for place like multi step form where file upload happens with submitting the form with other inputs (eg: make a new claim page in member portal)
     */
    uploadAsFileSelect: {
      type: Boolean,
      default: false,
    },

    /**
     * Data that is passed to this prop will be emitted form this component
     */
    dataToEmitWith: {},

    /**
     * A list stored the file index which file has been uploaded successfully
     */
    removedFilesIndexList: {
      type: Array,
    },
  },

  components: {
    Spinner,
    Alert,
  },

  data() {
    return {
      files: [],
      fileImgs: [],
      totalFileSize: 0,
      LoadingStatus,
      fileTypes: [],
      fileNames: [],
      fileInvalid: this.uploadedFiles.fileInvalid,
    };
  },

  setup() {
    return { v$: useVuelidate() };
  },

  validations() {
    return {
      files: {
        allFilesValid(files) {
          return files.every(file =>
            isValidFileUploadSize(file) && isValidFileUploadType(file) && isValidFileUploadName(file)
          );
        },
        isValidTotalFileUploadSize: { $validator: isValidTotalFileUploadSize },
      },
    };
  },

  methods: {
    isValidFileUploadSize,
    isValidFileUploadType,
    isValidFileUploadName,

    uploadFiles() {
      const extraData = {
        ...this.dataToEmitWith,
        fileImgs: this.fileImgs,
        totalFileSize: this.totalFileSize,
        fileInvalid: this.fileInvalid,
      };
      this.$emit('upload', this.files, extraData);
      this.v$.files.$touch();
      this.$emit('removeError', this.dataToEmitWith);
    },

    updateFileUploadError() {
      if (this.files) {
        let isInvalid = false;
        // eslint-disable-next-line consistent-return
        Array.from(this.files).forEach((file) => {
          if (!isValidFileUploadSize(file) || !isValidFileUploadType(file) || !isValidFileUploadName(file)) {
            isInvalid = true;
          }
        });
        this.fileInvalid = isInvalid;

        if (!this.fileInvalid) {
          this.fileInvalid = !isValidTotalFileUploadSize(this.files);
        }
      } else {
        this.fileInvalid = false;
      }
    },

    addSelectedFile(event) {
      const selected = event.target.files;

      Array.from(selected).forEach((file) => {
        this.files.push(file);
        this.totalFileSize += file.size;
        this.fileImgs.push(URL.createObjectURL(file));
        this.fileTypes.push(file.type);
        this.fileNames.push(file.name);
      });
      this.v$.files.$touch();
      this.updateFileUploadError();

      this.$refs['file-uploader'].value = '';
    },

    removeSelectedFile(index) {
      const file = this.files[index];
      this.files.splice(index, 1);
      this.fileImgs.splice(index, 1);
      this.fileTypes.splice(index, 1);
      this.fileNames.splice(index, 1);
      this.totalFileSize -= file.size;
      this.v$.files.$touch();
      this.updateFileUploadError();
    },

    getSelectedFileClass(index) {
      switch (true) {
        case this.uploadStatus === LoadingStatus.PENDING:
          return 'uploading';
        case this.fileImgs[index] !== null:
          return 'preview';
        default:
          return 'default';
      }
    },
  },

  watch: {
    totalFileSize() {
      if (this.uploadAsFileSelect) {
        this.uploadFiles();
      }
    },

    uploadStatus(newVal) {
      if (newVal === LoadingStatus.SUCCESS) {
        this.files = [];
        this.fileImgs = [];
        this.totalFileSize = 0;
      } else if (newVal !== LoadingStatus.ERROR) {
        // Removed uploaded file images
        this.removedFilesIndexList.forEach((index) => {
          this.fileImgs.splice(index, 1);
          this.fileTypes.splice(index, 1);
        });
      }
    },
  },

  mounted() {
    if (!this.uploadAsFileSelect && this.uploadedFiles.fileNames.length !== 0) {
      this.totalFileSize = this.uploadedFiles.size;
    }

    if (this.uploadAsFileSelect) {
      this.totalFileSize = this.uploadedFiles.size;
      this.files = this.uploadedFiles.files;
      this.fileImgs = this.uploadedFiles.fileImgs;
      this.uploadedFiles.files.forEach((file) => {
        this.fileNames.push(file.name);
        this.fileTypes.push(file.type);
      });
    }
  },
};
</script>
<style lang="scss">
.file-select-wrapper {
  .spinner {
    right: 7px;
  }

  .button-container {
    display: block;
    margin-top: 30px;

    &.reduce-margin {
      margin-top: 15px; // Total margin will be 20px when there are no files (5px from text above)
    }

    label {
      margin: unset;
      width: 100%;
    }

    .upload-button {
      justify-self: end;
      max-width: unset;
    }

    .button:not(.non-button-style).secondary {
      max-width: unset;
    }
  }
  .file-container {
    display: grid;
    margin-bottom: 15px;
    margin-top: 5px;
    grid-template-rows: 1fr;
    row-gap: 15px;

    #selected-container {
      margin-top: 35px;
    }

    .file-name {
      color: $GREY_300;
      font-weight: unset;
      display: block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .file {
      box-shadow: 0px 1px 8px $GREY_300;
      display: grid;
      grid-template-columns: 62px 5fr 0.5fr;
      align-items: center;
      gap: 20px;

      &.uploading {
        grid-template-columns: 1fr 1fr 5fr 0.5fr;
      }

      .default {
        height: 100%;
        &::before {
          background-image: url('/assets/public/images/icon_file.svg');
          background-color: $GREY_300;
          background-size: 47%;
          content: '';
          display: block;
          background-repeat: no-repeat;
          min-height: 74px;
          height: 100%;
          width: 62px;
          background-position: center;

          @include tablet-and-below {
            margin-right: 20px;
          }
        }
      }

      .uploaded {
        &::before {
          background-image: url('/assets/public/images/icon_included.svg');
          background-size: 47%;
          content: '';
          display: block;
          background-repeat: no-repeat;
          height: 74px;
          width: 62px;
          background-position: center;

          @include tablet-and-below {
            margin-right: 20px;
          }
        }
      }

      &.upload-error {
        &::before {
          background-image: url('/assets/public/images/icon_error.svg');
          background-size: auto;
          background-color: $ERROR;
          content: '';
          display: block;
          background-repeat: no-repeat;
          height: 100%;
          width: 62px;
          background-position: center;
        }
        border: 1px solid $ERROR;
        font-weight: unset;
        overflow: hidden;
        text-overflow: ellipsis;

        .error-status {
          color: $ERROR;
          font-weight: unset;
          font-size: font-size(-1);
          grid-row: 2;
          overflow: hidden;
          text-overflow: ellipsis;
          min-width: 0;
          overflow-wrap: break-word;
        }
      }

      .preview {
        min-height: 74px;
        height: 100%;
        width: 62px;
        box-shadow: 0px 0px 10px $GREY_300;
        margin-right: 20px;
        display: block;
        background-position: center;
      }

      .uploading {
        font-weight: unset;
        margin-right: 5px;
      }

      .upload-status {
        justify-content: unset;
      }

      .file-block {
        display: grid;
        column-gap: 5px;
        row-gap: 5px;
        margin-right: 0;
        padding: 20px 0px;

        .file-name {
          color: unset;
          font-weight: $BOLD;
          grid-row: 1;
        }

        .upload-status {
          font-weight: unset;
          font-size: font-size(-1);
          grid-row: 2;
          grid-column: 1;
        }
      }

      a {
        $DIMENSION: 38px;
        &.remove-file {
          color: $GREY_600;
          height: $DIMENSION;
          min-width: $DIMENSION;
          line-height: $DIMENSION;
          background-color: transparent;
          font-size: font-size(3);
          font-weight: unset;
          right: 10px;
          text-align: center;
          text-decoration: none;

          &::before {
            content: '\00d7'; // This is the unicode for the multiplication symbol.
          }

          &:hover,
          &:focus {
            color: $CRIMSON;
            border-radius: 50%;
            background-color: $GREY_500;
          }

          &:active {
            color: $GREY_800;
            background-color: transparent;
          }
        }
      }
    }
  }

  p.no-file {
    margin: 0px 0px 0px 1rem !important;
    padding: rem(15) 0;
    display: inline-block !important;
  }
}

:deep(input[type='file']) {
  &:focus + * {
    label.button.secondary {
      background-color: $LIGHT_BLUE_TINT;
    }
  }
}

.inline-message {
  margin-top: 30px;
}

.icon-container {
  display: inline-flex !important;
  align-items: center;
  vertical-align: bottom;

  svg {
    width: 24px;
    height: 24px;
  }
}
</style>
